import { SchoolAutocomplete } from "@/components/SchoolAutocomplete";
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useSearch } from "wouter";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { User, Lock, Mail, School, CreditCard, Calendar, AlertCircle, AlertTriangle, Camera, Users, CheckCircle2, Accessibility, Plus, Trash2, Star, Bell } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SimpleImageUploader } from "@/components/SimpleImageUploader";
import { ProfilePhotoViewer } from "@/components/ProfilePhotoViewer";
import { isPushSupported, getExistingSubscription, enablePush, disablePush } from "@/lib/webPush";

import { stripePromise } from "@/lib/stripe";

const fmtLongDate = (input: string | number | Date) =>
  new Date(input).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").or(z.literal("")),
  school: z.string().optional(),
  bio: z.string().max(160, "Bio must be 160 characters or less").optional(),
  gender: z.string().optional(),
  dateOfBirth: z.string().optional(),
  parentFirstName: z.string().optional(),
  parentLastName: z.string().optional(),
  parentEmail: z.string().email("Invalid parent email address").or(z.literal("")).optional(),
  parentPhone: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

type ActiveTab = "profile" | "password" | "billing" | "accessibility" | "notifications";

export default function ProfileSettings() {
  const { toast } = useToast();
  const { user: authUser, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const activeTab: ActiveTab = (() => {
    const t = new URLSearchParams(search).get("tab");
    if (t === "password" || t === "billing" || t === "accessibility" || t === "notifications") return t;
    return "profile";
  })();
  const setActiveTab = (tab: ActiveTab) => setLocation(`/settings?tab=${tab}`);

  useEffect(() => {
    if (!authLoading && !authUser) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      setLocation('/login?returnTo=' + returnTo);
    }
  }, [authUser, authLoading, setLocation]);

  const { data: userData, isLoading: userLoading } = useQuery<{ user: any }>({
    queryKey: ["/api/auth/me"],
  });

  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery<{
    isActive: boolean;
    subscriptionStatus: string;
    subscriptionPlan: string;
    subscriptionEndDate: string;
    subscribedGrade: number;
    subscriptionCancelAtPeriodEnd: boolean;
    retentionSaveOfferShown: boolean;
    retentionPauseOfferShown: boolean;
    retentionWinbackSent: boolean;
  }>({
    queryKey: ["/api/subscription/status"],
  });

  const user = userData?.user;

  const [showAddCard, setShowAddCard] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(false);

  const { data: paymentMethodsData, isLoading: pmLoading } = useQuery<{
    paymentMethods: any[];
    defaultPaymentMethodId: string | null;
  }>({
    queryKey: ["/api/billing/payment-methods"],
    enabled: activeTab === "billing",
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (pmId: string) => {
      const res = await apiRequest("POST", `/api/billing/payment-methods/${pmId}/set-default`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
      toast({ title: "Default card updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update default", description: e.message, variant: "destructive" }),
  });

  const removeCardMutation = useMutation({
    mutationFn: async (pmId: string) => {
      const res = await apiRequest("DELETE", `/api/billing/payment-methods/${pmId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
      toast({ title: "Card removed" });
    },
    onError: (e: any) => toast({ title: "Could not remove card", description: e.message, variant: "destructive" }),
  });

  const retryPaymentMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/subscription/retry-payment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "Payment successful!", description: "Your subscription has been restored." });
    },
    onError: (e: any) => toast({ title: "Payment failed", description: e.message || "Please update your card and try again.", variant: "destructive" }),
  });

  // Plan upgrade options (e.g. monthly -> yearly)
  const { data: upgradeOptionsData } = useQuery<{
    currentPlan: string | null;
    currentAmount: number | null;
    currency: string;
    grade: number | null;
    options: Array<{
      plan: "yearly" | "monthly";
      priceId: string;
      amount: number;
      currency: string;
      savingsAmount?: number;
      savingsPercent?: number;
    }>;
  }>({
    queryKey: ["/api/subscription/upgrade-options"],
    enabled: activeTab === "billing",
  });

  const [planSwitchOpen, setPlanSwitchOpen] = useState(false);

  const switchPlanMutation = useMutation({
    mutationFn: async (newPriceId: string) => {
      const res = await apiRequest("POST", "/api/subscription/switch-plan", { newPriceId });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/upgrade-options"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      setPlanSwitchOpen(false);
      toast({
        title: "You're on the Annual plan",
        description: data?.newPlan === "yearly"
          ? "Stripe charged the prorated difference and your renewal date moved to a year from today."
          : "Plan updated.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't switch plan",
        description: e.message || "Try again or contact support.",
        variant: "destructive",
      }),
  });

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      school: user?.school || "",
      bio: (user as any)?.bio || "",
      gender: user?.gender || "",
      dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : "",
      parentFirstName: user?.parentFirstName || "",
      parentLastName: user?.parentLastName || "",
      parentEmail: user?.parentEmail || "",
      parentPhone: user?.parentPhone || "",
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (user) {
      profileForm.reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        school: user.school || "",
        gender: user.gender || "",
        dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : "",
        parentFirstName: user.parentFirstName || "",
        parentLastName: user.parentLastName || "",
        parentEmail: user.parentEmail || "",
        parentPhone: user.parentPhone || "",
      });
    }
  }, [user, profileForm]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      return await apiRequest("PUT", "/api/profile/update", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      return await apiRequest("PUT", "/api/profile/password", data);
    },
    onSuccess: () => {
      passwordForm.reset();
      toast({
        title: "Password changed",
        description: "Your password has been changed successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Password change failed",
        description: error.message || "Failed to change password. Please check your current password.",
        variant: "destructive",
      });
    },
  });

  const updateProfilePhotoMutation = useMutation({
    mutationFn: async (photoURL: string) => {
      const response = await apiRequest("PUT", "/api/profile/photo", { photoURL });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/level"] });
      
      if (data.bonusAwarded) {
        toast({
          title: "Profile photo updated + 25 XP Bonus!",
          description: "Congratulations! You earned 25 XP for uploading your first profile photo!",
        });
      } else {
        toast({
          title: "Profile photo updated",
          description: "Your profile photo has been updated successfully",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Photo upload failed",
        description: error.message || "Failed to update profile photo. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handlePhotoUpload = async (file: File) => {
    try {
      // Upload via server (avoids CORS with DO Spaces)
      const formData = new FormData();
      formData.append("photo", file);

      const uploadResponse = await fetch("/api/profile/photo/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!uploadResponse.ok) {
        const err = await uploadResponse.json().catch(() => ({}));
        throw new Error(err.error || "Failed to upload photo");
      }

      const result = await uploadResponse.json();

      // Refresh user data to show new photo + XP
      if (result.bonusAwarded) {
        toast({
          title: "Profile photo updated + 25 XP Bonus!",
          description: "Congratulations! You earned 25 XP for uploading your first profile photo!",
        });
      } else {
        toast({
          title: "Profile photo updated",
          description: "Your profile photo has been updated successfully",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState<'reason' | 'matriculation' | 'save-offer' | 'pause-offer' | 'confirm' | 'done'>('reason');
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelOtherDetails, setCancelOtherDetails] = useState('');
  const [winbackCode, setWinbackCode] = useState<string | undefined>(undefined);
  const [pauseMonths, setPauseMonths] = useState<1 | 2 | 3>(1);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<'reason' | 'matriculation' | 'confirm'>('reason');
  const [deleteReason, setDeleteReason] = useState<string>('');
  const [deleteOtherDetails, setDeleteOtherDetails] = useState('');
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [deleteExcludeLeaderboard, setDeleteExcludeLeaderboard] = useState(false);

  const cancelReasons = [
    "Too expensive / can't afford it right now",
    "Not using it enough",
    "Found another platform that works better",
    "Technical issues or problems with the platform",
    "Completed my studies / no longer need it",
    "Content doesn't match my needs",
    "Other",
  ];

  const getNextStepAfterReason = () => {
    const isCompletedStudies = cancelReason === "Completed my studies / no longer need it";
    if (isCompletedStudies) return 'matriculation';
    const isActive = subscriptionData?.subscriptionStatus === 'active';
    const isTooExpensive = cancelReason === "Too expensive / can't afford it right now";
    if (isActive && isTooExpensive && !subscriptionData?.retentionSaveOfferShown) return 'save-offer';
    if (isActive && !subscriptionData?.retentionPauseOfferShown) return 'pause-offer';
    return 'confirm';
  };

  const getNextCancelStepAfterMatriculation = () => {
    const isActive = subscriptionData?.subscriptionStatus === 'active';
    if (isActive && !subscriptionData?.retentionPauseOfferShown) return 'pause-offer';
    return 'confirm';
  };

  const getNextStepAfterSaveOffer = () => {
    if (!subscriptionData?.retentionPauseOfferShown) return 'pause-offer';
    return 'confirm';
  };

  const markOfferSeenMutation = useMutation({
    mutationFn: async (offer: 'save' | 'pause') =>
      apiRequest("POST", "/api/subscription/mark-offer-seen", { offer }),
  });

  const saveOfferMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/subscription/save-offer"),
    onSuccess: () => {
      toast({ title: "Discount applied!", description: "40% off has been applied to your next payment. Thanks for staying with EduFocal!" });
      setCancelDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to apply discount", description: error.message || "Please try again.", variant: "destructive" });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: async (months: number) => apiRequest("POST", "/api/subscription/pause", { months }),
    onSuccess: () => {
      toast({ title: "Subscription paused", description: `Your subscription is paused for ${pauseMonths} month${pauseMonths > 1 ? 's' : ''}. See you soon!` });
      setCancelDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to pause", description: error.message || "Please try again.", variant: "destructive" });
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/subscription/cancel", {
        reason: cancelReason,
        otherDetails: cancelReason === 'Other' ? cancelOtherDetails : undefined,
      });
    },
    onSuccess: (data: any) => {
      setCancelStep('done');
      if (data?.winbackCode) setWinbackCode(data.winbackCode);
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (error: any) => {
      toast({
        title: "Cancellation failed",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const matriculationMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/profile/request-matriculation");
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Grade upgrade requested!",
        description: `We've received your request to move to Grade ${data.requestedGrade}. Our team will be in touch shortly.`,
      });
      setCancelDialogOpen(false);
      setDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Request failed",
        description: error.message || "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const requestDeletionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/profile/request-deletion", {
        reason: deleteReason === 'Other' ? deleteOtherDetails : deleteReason,
        excludeFromLeaderboard: deleteExcludeLeaderboard,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Account scheduled for deletion",
        description: "Your account will be permanently deleted in 15 days. You can cancel this from Settings at any time.",
      });
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription/status"] });
    },
    onError: (error: any) => {
      toast({
        title: "Request failed",
        description: error.message || "Failed to request deletion. Please try again.",
        variant: "destructive",
      });
    },
  });

  const cancelDeletionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/profile/cancel-deletion");
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Deletion cancelled",
        description: "Your account has been restored. You'll need to re-subscribe to access content again.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel deletion",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    updatePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">Manage your account settings and preferences</p>
        </div>

        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-4 py-2 font-medium transition-colors hover-elevate active-elevate-2 ${
              activeTab === "profile"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="button-tab-profile"
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`px-4 py-2 font-medium transition-colors hover-elevate active-elevate-2 ${
              activeTab === "password"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="button-tab-password"
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Password
          </button>
          <button
            onClick={() => setActiveTab("billing")}
            className={`px-4 py-2 font-medium transition-colors hover-elevate active-elevate-2 ${
              activeTab === "billing"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="button-tab-billing"
          >
            <CreditCard className="w-4 h-4 inline mr-2" />
            Billing
          </button>
          <button
            onClick={() => setActiveTab("accessibility")}
            className={`px-4 py-2 font-medium transition-colors hover-elevate active-elevate-2 ${
              activeTab === "accessibility"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="button-tab-accessibility"
          >
            <Accessibility className="w-4 h-4 inline mr-2" />
            Accessibility
          </button>
          <button
            onClick={() => setActiveTab("notifications")}
            className={`px-4 py-2 font-medium transition-colors hover-elevate active-elevate-2 ${
              activeTab === "notifications"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground"
            }`}
            data-testid="button-tab-notifications"
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Notifications
          </button>
        </div>

        {activeTab === "profile" && (
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your profile details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 pb-6">
                <div className="relative">
                  <Avatar
                    className={`h-24 w-24 ${user?.profilePhotoUrl ? 'cursor-pointer ring-2 ring-transparent hover:ring-primary transition-all' : ''}`}
                    onClick={() => user?.profilePhotoUrl && setViewingPhoto(true)}
                  >
                    <AvatarImage
                      src={user?.profilePhotoUrl || ''}
                      alt={`${user?.firstName} ${user?.lastName}`}
                      data-testid="img-profile-photo"
                    />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">Profile Photo</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload a photo to personalize your account
                  </p>
                  <SimpleImageUploader
                    onUpload={handlePhotoUpload}
                    buttonText={user?.profilePhotoUrl ? 'Change Photo' : 'Upload Photo'}
                    variant="outline"
                    size="sm"
                  />
                </div>
              </div>
              
              <Separator className="mb-6" />

              <Form {...profileForm}>
                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={profileForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                className="pl-10" 
                                placeholder="Enter your first name"
                                data-testid="input-firstname"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={profileForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                className="pl-10" 
                                placeholder="Enter your last name"
                                data-testid="input-lastname"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={profileForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="email" 
                              className="pl-10" 
                              placeholder="your.email@example.com"
                              data-testid="input-email"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="school"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>School Name (Optional)</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <School className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <SchoolAutocomplete
                              id="school"
                              name={field.name}
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              className="pl-10"
                              placeholder="Your school name"
                              data-testid="input-school"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bio (Optional)</FormLabel>
                        <FormControl>
                          <div className="space-y-1">
                            <Textarea
                              {...field}
                              placeholder="Tell classmates about yourself... (160 characters max)"
                              className="resize-none h-20"
                              maxLength={160}
                            />
                            <p className="text-xs text-muted-foreground text-right">{(field.value ?? "").length}/160</p>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Student Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={profileForm.control}
                        name="gender"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Gender (Optional)</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-gender">
                                  <SelectValue placeholder="Select gender" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="male">Boy</SelectItem>
                                <SelectItem value="female">Girl</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date of Birth (Optional)</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input 
                                  {...field} 
                                  type="date"
                                  className="pl-10" 
                                  data-testid="input-dob"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Parent/Guardian Information</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      This information helps us with prize delivery and important communications
                    </p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={profileForm.control}
                          name="parentFirstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Parent First Name (Optional)</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                  <Input 
                                    {...field} 
                                    className="pl-10" 
                                    placeholder="Parent's first name"
                                    data-testid="input-parent-firstname"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={profileForm.control}
                          name="parentLastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Parent Last Name (Optional)</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                  <Input 
                                    {...field} 
                                    className="pl-10" 
                                    placeholder="Parent's last name"
                                    data-testid="input-parent-lastname"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={profileForm.control}
                        name="parentEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Parent Email (Optional)</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input 
                                  {...field} 
                                  type="email" 
                                  className="pl-10" 
                                  placeholder="parent@example.com"
                                  data-testid="input-parent-email"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={profileForm.control}
                        name="parentPhone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Parent Phone Number (Optional)</FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                type="tel" 
                                placeholder="876XXXXXXXX"
                                data-testid="input-parent-phone"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={updateProfileMutation.isPending}
                      data-testid="button-save-profile"
                    >
                      {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {activeTab === "password" && (
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                  <FormField
                    control={passwordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="password" 
                              className="pl-10" 
                              placeholder="Enter current password"
                              data-testid="input-current-password"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="password" 
                              className="pl-10" 
                              placeholder="Enter new password (min 6 characters)"
                              data-testid="input-new-password"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="password" 
                              className="pl-10" 
                              placeholder="Confirm new password"
                              data-testid="input-confirm-password"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={updatePasswordMutation.isPending}
                      data-testid="button-save-password"
                    >
                      {updatePasswordMutation.isPending ? "Changing..." : "Change Password"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )}

        {activeTab === "billing" && (
          <div className="space-y-6">

          {/* Payment Methods Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>Cards saved for billing and renewals</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowAddCard(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Card
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pmLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : !paymentMethodsData?.paymentMethods.length ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No saved cards yet. Add a card to enable automatic billing.
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentMethodsData.paymentMethods.map((pm: any) => {
                    const isDefault = pm.id === paymentMethodsData.defaultPaymentMethodId;
                    return (
                      <div key={pm.id} className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
                        <CreditCard className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium capitalize">{pm.card.brand}</span>
                            <span className="text-muted-foreground">•••• {pm.card.last4}</span>
                            <span className="text-sm text-muted-foreground">
                              {pm.card.exp_month.toString().padStart(2, '0')}/{pm.card.exp_year}
                            </span>
                            {isDefault && (
                              <Badge className="text-xs">Default</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isDefault && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDefaultMutation.mutate(pm.id)}
                              disabled={setDefaultMutation.isPending}
                              title="Set as default"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCardMutation.mutate(pm.id)}
                            disabled={removeCardMutation.isPending || paymentMethodsData.paymentMethods.length <= 1}
                            title={paymentMethodsData.paymentMethods.length <= 1 ? "At least one card must remain on your account" : "Remove card"}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Card Dialog */}
          {stripePromise && (
            <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Card</DialogTitle>
                  <DialogDescription>Enter your card details to save it for billing and renewals.</DialogDescription>
                </DialogHeader>
                <Elements stripe={stripePromise}>
                  <AddCardForm
                    onSuccess={() => {
                      setShowAddCard(false);
                      queryClient.invalidateQueries({ queryKey: ["/api/billing/payment-methods"] });
                    }}
                    onCancel={() => setShowAddCard(false)}
                  />
                </Elements>
              </DialogContent>
            </Dialog>
          )}

          {/* Subscription Card */}
          <Card>
            <CardHeader>
              <CardTitle>Billing & Subscription</CardTitle>
              <CardDescription>Manage your subscription and payment details</CardDescription>
            </CardHeader>
            <CardContent>
              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : subscriptionData?.isActive ? (
                <div className="space-y-6">
                  {subscriptionData.subscriptionStatus === 'trial' && (
                    <div className="flex items-start gap-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg" data-testid="alert-trial-notice">
                      <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">You're on a Free Trial</p>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          You have full access during your trial period. If you don't cancel before your trial ends on{' '}
                          <span className="font-medium">
                            {fmtLongDate(subscriptionData.subscriptionEndDate)}
                          </span>
                          , your card will be charged automatically. If you cancel before the trial ends, you will not be charged.
                        </p>
                      </div>
                    </div>
                  )}

                  {subscriptionData.subscriptionCancelAtPeriodEnd ? (
                    <div className="flex items-start gap-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg" data-testid="alert-cancellation-notice">
                      <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1 flex-1">
                        <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                          {subscriptionData.subscriptionStatus === 'trial' ? 'Trial Cancelled' : 'Subscription Cancelling'}
                        </p>
                        <p className="text-sm text-orange-800 dark:text-orange-200">
                          {subscriptionData.subscriptionStatus === 'trial'
                            ? <>Your trial has been cancelled and you will not be charged. You'll continue to have access until{' '}
                                <span className="font-medium" data-testid="text-cancellation-date">
                                  {fmtLongDate(subscriptionData.subscriptionEndDate)}
                                </span>.
                              </>
                            : <>Your subscription has been cancelled and will end on{' '}
                                <span className="font-medium" data-testid="text-cancellation-date">
                                  {fmtLongDate(subscriptionData.subscriptionEndDate)}
                                </span>. You'll continue to have access until then.
                              </>
                          }
                        </p>
                      </div>
                    </div>
                  ) : null}

                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold" data-testid="text-subscription-status">
                          {subscriptionData.subscriptionStatus === 'trial'
                            ? (subscriptionData.subscriptionCancelAtPeriodEnd ? 'Trial Cancelled' : 'Free Trial')
                            : (subscriptionData.subscriptionCancelAtPeriodEnd ? 'Active Until Cancellation' : 'Active Subscription')}
                        </h3>
                        <Badge className={subscriptionData.subscriptionStatus === 'trial'
                          ? "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20"
                          : "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20"
                        }>
                          {subscriptionData.subscriptionStatus === 'trial' ? 'Trial' : 'Active'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        You have full access to all Grade {subscriptionData.subscribedGrade} content
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">Plan</span>
                      </div>
                      <span className="text-muted-foreground capitalize" data-testid="text-subscription-plan">
                        {subscriptionData.subscriptionStatus === 'trial'
                          ? `Free Trial (${subscriptionData.subscriptionPlan === 'monthly' ? 'Monthly' : 'Yearly'} after trial)`
                          : (subscriptionData.subscriptionPlan === 'monthly' ? 'Monthly (JMD $2,500)' : 'Yearly (JMD $10,000)')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">
                          {subscriptionData.subscriptionStatus === 'trial' ? 'Trial Ends' : 'Valid Until'}
                        </span>
                      </div>
                      <span className="text-muted-foreground" data-testid="text-subscription-end-date">
                        {fmtLongDate(subscriptionData.subscriptionEndDate)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">Time Remaining</span>
                      </div>
                      <span className="text-muted-foreground" data-testid="text-time-remaining">
                        {(() => {
                          const endDate = new Date(subscriptionData.subscriptionEndDate);
                          const now = new Date();
                          const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysRemaining < 0) return 'Expired';
                          if (daysRemaining === 0) return 'Less than 1 day';
                          if (daysRemaining === 1) return '1 day';
                          return `${daysRemaining} days`;
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Plan switch (monthly -> yearly) */}
                  {!subscriptionData.subscriptionCancelAtPeriodEnd
                    && upgradeOptionsData
                    && upgradeOptionsData.options.length > 0 && (
                    <>
                      <Separator />
                      <div
                        className="rounded-lg border border-green-500/30 bg-green-500/5 p-4 md:p-5"
                        data-testid="card-plan-upgrade"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-md bg-green-500/15 flex items-center justify-center flex-shrink-0">
                            <Star className="w-5 h-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="font-semibold text-base">Save by switching to Annual</h4>
                              {upgradeOptionsData.options[0].savingsPercent != null && (
                                <Badge className="bg-green-600 text-white border-green-700 hover:bg-green-700">
                                  Save {upgradeOptionsData.options[0].savingsPercent}%
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              You're paying JMD ${(upgradeOptionsData.currentAmount ?? 2500).toLocaleString()}/month.
                              Switch to Annual at JMD ${upgradeOptionsData.options[0].amount.toLocaleString()}/year and save
                              JMD ${(upgradeOptionsData.options[0].savingsAmount || 0).toLocaleString()} compared to paying monthly.
                              We'll prorate today's switch.
                            </p>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => setPlanSwitchOpen(true)}
                              disabled={switchPlanMutation.isPending}
                              data-testid="button-switch-plan-yearly"
                            >
                              Switch to Annual
                            </Button>
                          </div>
                        </div>
                      </div>

                      <AlertDialog open={planSwitchOpen} onOpenChange={setPlanSwitchOpen}>
                        <AlertDialogContent data-testid="dialog-confirm-plan-switch">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Switch to the Annual plan?</AlertDialogTitle>
                            <AlertDialogDescription asChild>
                              <div className="space-y-2 text-sm text-muted-foreground">
                                <p>
                                  Your subscription will switch from Monthly to Annual right away. Stripe will
                                  charge or credit the difference based on what's left in your current month.
                                </p>
                                <p>
                                  New annual price: <strong>JMD ${upgradeOptionsData.options[0].amount.toLocaleString()}/year</strong>
                                  {upgradeOptionsData.options[0].savingsAmount != null && (
                                    <> (saves you JMD ${upgradeOptionsData.options[0].savingsAmount.toLocaleString()} vs paying monthly).</>
                                  )}
                                </p>
                                <p>Your renewal date will move to one year from today.</p>
                              </div>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep Monthly</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-green-600 hover:bg-green-700"
                              disabled={switchPlanMutation.isPending}
                              onClick={(e) => {
                                e.preventDefault();
                                switchPlanMutation.mutate(upgradeOptionsData.options[0].priceId);
                              }}
                            >
                              {switchPlanMutation.isPending ? "Switching…" : "Confirm switch"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}

                  {!subscriptionData.subscriptionCancelAtPeriodEnd && (
                    <>
                      <Separator />

                      <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="space-y-2 flex-1">
                          <p className="text-sm font-medium">
                            {subscriptionData.subscriptionStatus === 'trial' ? 'Cancel Trial' : 'Cancel Subscription'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {subscriptionData.subscriptionStatus === 'trial'
                              ? 'If you cancel during your trial, you will not be charged. You\'ll still have access until your trial period ends.'
                              : 'You can cancel your subscription at any time. You\'ll continue to have access until the end of your current billing period.'}
                          </p>
                          <AlertDialog open={cancelDialogOpen} onOpenChange={(open) => {
                            setCancelDialogOpen(open);
                            if (!open) {
                              setCancelStep('reason');
                              setCancelReason('');
                              setCancelOtherDetails('');
                              setWinbackCode(undefined);
                              setPauseMonths(1);
                            }
                          }}>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                data-testid="button-cancel-subscription"
                              >
                                {subscriptionData.subscriptionStatus === 'trial' ? 'Cancel Trial' : 'Cancel Subscription'}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="max-w-md">
                              {cancelStep === 'reason' && (
                                <>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Why are you leaving?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Please let us know why you'd like to cancel. Your feedback helps us improve.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="space-y-2 py-2">
                                    {cancelReasons.map((reason) => (
                                      <button
                                        key={reason}
                                        type="button"
                                        onClick={() => setCancelReason(reason)}
                                        className={`w-full text-left px-3 py-2.5 rounded-md text-sm border transition-colors ${
                                          cancelReason === reason
                                            ? 'border-primary bg-primary/10 text-foreground'
                                            : 'border-border hover-elevate text-foreground'
                                        }`}
                                        data-testid={`reason-${reason.toLowerCase().replace(/[^a-z]/g, '-')}`}
                                      >
                                        {reason}
                                      </button>
                                    ))}
                                    {cancelReason === 'Other' && (
                                      <Textarea
                                        placeholder="Please tell us more..."
                                        value={cancelOtherDetails}
                                        onChange={(e) => setCancelOtherDetails(e.target.value)}
                                        className="mt-2"
                                        data-testid="input-other-details"
                                      />
                                    )}
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="button-cancel-dialog-cancel">
                                      {subscriptionData.subscriptionStatus === 'trial' ? 'Keep Trial' : 'Keep Subscription'}
                                    </AlertDialogCancel>
                                    <Button
                                      variant="destructive"
                                      disabled={!cancelReason || (cancelReason === 'Other' && !cancelOtherDetails.trim())}
                                      onClick={() => setCancelStep(getNextStepAfterReason())}
                                      data-testid="button-next-confirm"
                                    >
                                      Continue
                                    </Button>
                                  </AlertDialogFooter>
                                </>
                              )}

                              {cancelStep === 'matriculation' && (
                                <>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Move to the next grade?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Congratulations on completing your studies! Did you know your EduFocal Academy account can be moved to the next grade level? Continue your learning journey in Grade {(user?.grade || 6) + 1}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="py-4 text-center space-y-3">
                                    <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-2 text-sm font-medium">
                                      Grade {user?.grade || 6} → Grade {(user?.grade || 6) + 1}
                                    </div>
                                    <p className="text-sm text-muted-foreground">Our team will upgrade your account and let you know when it's ready.</p>
                                  </div>
                                  <AlertDialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setCancelStep(getNextCancelStepAfterMatriculation())}
                                    >
                                      No, continue cancelling
                                    </Button>
                                    <Button
                                      onClick={() => matriculationMutation.mutate()}
                                      disabled={matriculationMutation.isPending}
                                    >
                                      {matriculationMutation.isPending ? 'Requesting...' : 'Request Grade Upgrade'}
                                    </Button>
                                  </AlertDialogFooter>
                                </>
                              )}

                              {cancelStep === 'save-offer' && (
                                <>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Wait, before you go</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      We understand things can get tight. As a loyal EduFocal subscriber, we'd like to offer you 40% off your next payment, no strings attached.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="py-4 text-center space-y-1">
                                    <p className="text-4xl font-bold text-primary">40% OFF</p>
                                    <p className="text-sm text-muted-foreground">Applied to your very next payment</p>
                                    <p className="text-xs text-muted-foreground">This is a one-time offer available once per account.</p>
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <Button
                                      onClick={() => saveOfferMutation.mutate()}
                                      disabled={saveOfferMutation.isPending}
                                      data-testid="button-accept-save-offer"
                                    >
                                      {saveOfferMutation.isPending ? 'Applying...' : 'Yes, apply 40% discount'}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      disabled={saveOfferMutation.isPending}
                                      onClick={() => {
                                        markOfferSeenMutation.mutate('save');
                                        setCancelStep(getNextStepAfterSaveOffer());
                                      }}
                                      data-testid="button-decline-save-offer"
                                    >
                                      No thanks, continue to cancel
                                    </Button>
                                  </div>
                                </>
                              )}

                              {cancelStep === 'pause-offer' && (
                                <>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Need a break instead?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Pause your subscription for up to 3 months. Your progress is saved and billing resumes automatically. You won't be charged while paused.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <div className="space-y-2 py-2">
                                    {([1, 2, 3] as const).map((m) => (
                                      <button
                                        key={m}
                                        type="button"
                                        onClick={() => setPauseMonths(m)}
                                        className={`w-full text-left px-3 py-2.5 rounded-md text-sm border transition-colors ${
                                          pauseMonths === m
                                            ? 'border-primary bg-primary/10 text-foreground'
                                            : 'border-border hover-elevate text-foreground'
                                        }`}
                                        data-testid={`pause-months-${m}`}
                                      >
                                        Pause for {m} month{m > 1 ? 's' : ''}
                                      </button>
                                    ))}
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <Button
                                      onClick={() => pauseMutation.mutate(pauseMonths)}
                                      disabled={pauseMutation.isPending}
                                      data-testid="button-accept-pause"
                                    >
                                      {pauseMutation.isPending ? 'Pausing...' : `Pause for ${pauseMonths} month${pauseMonths > 1 ? 's' : ''}`}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      disabled={pauseMutation.isPending}
                                      onClick={() => {
                                        markOfferSeenMutation.mutate('pause');
                                        setCancelStep('confirm');
                                      }}
                                      data-testid="button-decline-pause"
                                    >
                                      No thanks, I want to cancel
                                    </Button>
                                  </div>
                                </>
                              )}

                              {cancelStep === 'confirm' && (
                                <>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {subscriptionData.subscriptionStatus === 'trial'
                                        ? <>This will cancel your free trial. You will not be charged. You'll continue to have access to Grade {subscriptionData.subscribedGrade} content until{' '}
                                            <span className="font-medium">
                                              {fmtLongDate(subscriptionData.subscriptionEndDate)}
                                            </span>.
                                          </>
                                        : <>This will cancel your subscription. You'll continue to have access to Grade {subscriptionData.subscribedGrade} content until{' '}
                                            <span className="font-medium">
                                              {fmtLongDate(subscriptionData.subscriptionEndDate)}
                                            </span>.
                                          </>
                                      }
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setCancelStep('reason')}
                                      data-testid="button-back-to-reason"
                                    >
                                      Back
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      onClick={() => cancelSubscriptionMutation.mutate()}
                                      disabled={cancelSubscriptionMutation.isPending}
                                      data-testid="button-confirm-cancel-subscription"
                                    >
                                      {cancelSubscriptionMutation.isPending ? "Cancelling..." : "Yes, Cancel"}
                                    </Button>
                                  </AlertDialogFooter>
                                </>
                              )}

                              {cancelStep === 'done' && (
                                <>
                                  <AlertDialogHeader>
                                    <div className="flex justify-center mb-2">
                                      <CheckCircle2 className="w-12 h-12 text-green-500" />
                                    </div>
                                    <AlertDialogTitle className="text-center">
                                      {subscriptionData.subscriptionStatus === 'trial' ? 'Trial Cancelled' : 'Subscription Cancelled'}
                                    </AlertDialogTitle>
                                    <AlertDialogDescription className="text-center">
                                      Your {subscriptionData.subscriptionStatus === 'trial' ? 'trial' : 'subscription'} has been cancelled. You'll continue to have access until{' '}
                                      <span className="font-medium">
                                        {fmtLongDate(subscriptionData.subscriptionEndDate)}
                                      </span>.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  {winbackCode && (
                                    <div className="mx-auto w-full rounded-md border bg-muted px-4 py-3 text-center space-y-1">
                                      <p className="text-sm text-muted-foreground">Come back any time. 20% off your next subscription:</p>
                                      <p className="text-lg font-mono font-bold tracking-widest">{winbackCode}</p>
                                      <p className="text-xs text-muted-foreground">Valid for 30 days · One use only</p>
                                    </div>
                                  )}
                                  <AlertDialogFooter className="sm:justify-center">
                                    <Button
                                      onClick={() => setCancelDialogOpen(false)}
                                      data-testid="button-done"
                                    >
                                      Done
                                    </Button>
                                  </AlertDialogFooter>
                                </>
                              )}
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : subscriptionData?.subscriptionStatus === 'past_due' ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <p className="text-sm font-semibold text-red-900 dark:text-red-100">Payment Failed</p>
                      <p className="text-sm text-red-800 dark:text-red-200">
                        We couldn't process your last payment. Update your payment method below to restore full access.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => retryPaymentMutation.mutate()}
                      disabled={retryPaymentMutation.isPending}
                    >
                      {retryPaymentMutation.isPending ? 'Retrying...' : 'Retry Payment'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAddCard(true)}
                    >
                      Update Card
                    </Button>
                  </div>
                  {showAddCard && stripePromise && (
                    <Elements stripe={stripePromise}>
                      <AddCardForm
                        onSuccess={() => {
                          setShowAddCard(false);
                          retryPaymentMutation.mutate();
                        }}
                        onCancel={() => setShowAddCard(false)}
                      />
                    </Elements>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                    <CreditCard className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" data-testid="text-no-subscription">No Active Subscription</h3>
                    <p className="text-muted-foreground mb-4">
                      Subscribe to access all PEP preparation materials for your grade.
                    </p>
                    <Button asChild data-testid="button-subscribe-now">
                      <a href="/subscribe">Subscribe Now</a>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Account: Danger Zone */}
          <Card className="border-destructive/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle className="text-destructive">Danger Zone</CardTitle>
              </div>
              <CardDescription>
                Permanently delete your account and all associated data.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.deletionRequestedAt ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200">
                          Account scheduled for deletion
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          Your account will be permanently deleted on{' '}
                          <strong>
                            {new Date(new Date(user.deletionRequestedAt).getTime() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                            })}
                          </strong>.
                          All your data, progress, and subscription will be removed.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    If you cancel the deletion, your account will be restored but your subscription has already been cancelled. You'll need to re-subscribe to regain access to content.
                  </p>
                  <Button
                    variant="outline"
                    className="border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20"
                    onClick={() => cancelDeletionMutation.mutate()}
                    disabled={cancelDeletionMutation.isPending}
                  >
                    {cancelDeletionMutation.isPending ? 'Restoring...' : 'Cancel Deletion and Keep My Account'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Once deleted, your account cannot be recovered. Your subscription will be cancelled immediately
                    and your data will be permanently removed after a 15-day grace period.
                  </p>
                  <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
                    setDeleteDialogOpen(open);
                    if (!open) {
                      setDeleteStep('reason');
                      setDeleteReason('');
                      setDeleteOtherDetails('');
                      setDeleteConfirmChecked(false);
                    }
                  }}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="max-w-md">
                      {deleteStep === 'reason' && (
                        <>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Why are you deleting your account?</AlertDialogTitle>
                            <AlertDialogDescription>
                              We're sorry to see you go. Please let us know why you'd like to delete your account.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="space-y-2 py-2">
                            {cancelReasons.map((reason) => (
                              <button
                                key={reason}
                                type="button"
                                onClick={() => setDeleteReason(reason)}
                                className={`w-full text-left px-3 py-2.5 rounded-md text-sm border transition-colors ${
                                  deleteReason === reason
                                    ? 'border-primary bg-primary/10 text-foreground'
                                    : 'border-border hover-elevate text-foreground'
                                }`}
                              >
                                {reason}
                              </button>
                            ))}
                            {deleteReason === 'Other' && (
                              <Textarea
                                placeholder="Please tell us more..."
                                value={deleteOtherDetails}
                                onChange={(e) => setDeleteOtherDetails(e.target.value)}
                                className="mt-2"
                              />
                            )}
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <Button
                              variant="destructive"
                              disabled={!deleteReason || (deleteReason === 'Other' && !deleteOtherDetails.trim())}
                              onClick={() => {
                                if (deleteReason === "Completed my studies / no longer need it") {
                                  setDeleteStep('matriculation');
                                } else {
                                  setDeleteStep('confirm');
                                }
                              }}
                            >
                              Continue
                            </Button>
                          </AlertDialogFooter>
                        </>
                      )}

                      {deleteStep === 'matriculation' && (
                        <>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Move to the next grade?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Congratulations on completing your studies! Did you know your EduFocal Academy account can be moved to the next grade level? Continue your learning journey in Grade {(user?.grade || 6) + 1}.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="py-4 text-center space-y-3">
                            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-2 text-sm font-medium">
                              Grade {user?.grade || 6} → Grade {(user?.grade || 6) + 1}
                            </div>
                            <p className="text-sm text-muted-foreground">Our team will upgrade your account and let you know when it's ready.</p>
                          </div>
                          <AlertDialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setDeleteStep('confirm')}
                            >
                              No, continue deleting
                            </Button>
                            <Button
                              onClick={() => matriculationMutation.mutate()}
                              disabled={matriculationMutation.isPending}
                            >
                              {matriculationMutation.isPending ? 'Requesting...' : 'Request Grade Upgrade'}
                            </Button>
                          </AlertDialogFooter>
                        </>
                      )}

                      {deleteStep === 'confirm' && (
                        <>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription asChild>
                              <div className="space-y-3">
                                <p>This action will:</p>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                  <li>Cancel your subscription immediately</li>
                                  <li>Schedule your account for permanent deletion in 15 days</li>
                                  <li>Remove all your progress, XP, streaks, and achievements</li>
                                </ul>
                                <p>You can cancel the deletion within the 15-day grace period from Settings.</p>
                              </div>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="flex items-start gap-2 py-2">
                            <Checkbox
                              id="delete-confirm"
                              checked={deleteConfirmChecked}
                              onCheckedChange={(checked) => setDeleteConfirmChecked(checked === true)}
                            />
                            <label htmlFor="delete-confirm" className="text-sm leading-tight cursor-pointer">
                              I understand my account will be permanently deleted after 15 days
                            </label>
                          </div>
                          <div className="flex items-start gap-2 py-2 border-t pt-3">
                            <Checkbox
                              id="delete-exclude-leaderboard"
                              checked={deleteExcludeLeaderboard}
                              onCheckedChange={(checked) => setDeleteExcludeLeaderboard(checked === true)}
                            />
                            <label htmlFor="delete-exclude-leaderboard" className="text-sm leading-tight cursor-pointer text-muted-foreground">
                              Also remove me from the public all-time leaderboard. By default, top scorers stay on as anonymized Hall of Fame entries (first name + last initial) even after deletion.
                            </label>
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <Button
                              variant="destructive"
                              disabled={!deleteConfirmChecked || requestDeletionMutation.isPending}
                              onClick={() => requestDeletionMutation.mutate()}
                            >
                              {requestDeletionMutation.isPending ? 'Processing...' : 'Delete My Account'}
                            </Button>
                          </AlertDialogFooter>
                        </>
                      )}
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === "accessibility" && (
          <AccessibilitySettings />
        )}

        {activeTab === "notifications" && (
          <NotificationSettings />
        )}
      </div>

      <ProfilePhotoViewer
        url={viewingPhoto && user?.profilePhotoUrl ? user.profilePhotoUrl : null}
        name={user ? `${user.firstName} ${user.lastName}` : ''}
        onClose={() => setViewingPhoto(false)}
      />
    </div>
  );
}

function AddCardForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        fontFamily: "Inter, system-ui, sans-serif",
        color: isDark ? "#e2e4e9" : "#2f3645",
        "::placeholder": { color: isDark ? "#9da3af" : "#6b7a96" },
        iconColor: isDark ? "#9da3af" : "#6b7a96",
      },
      invalid: {
        color: isDark ? "#f05252" : "#a60b0b",
        iconColor: isDark ? "#f05252" : "#a60b0b",
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    try {
      // Get a SetupIntent client secret from the server
      const res = await apiRequest("POST", "/api/billing/payment-methods/setup");
      const { clientSecret, error: serverError } = await res.json();
      if (serverError) throw new Error(serverError);

      // Confirm the card setup with Stripe
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Card element not found");

      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (stripeError) throw new Error(stripeError.message);

      // Promote the newly-attached card to default for the customer + every
      // non-cancelled subscription. Without this step Stripe keeps using the
      // old (failing) card and past_due users stay broken even after adding
      // a fresh card.
      const newPmId = typeof setupIntent?.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id;
      if (newPmId) {
        try {
          await apiRequest("POST", `/api/billing/payment-methods/${newPmId}/set-default`);
        } catch (e) {
          console.warn("Failed to promote new card to default", e);
        }
      }

      toast({ title: "Card added successfully" });
      onSuccess();
    } catch (err: any) {
      toast({ title: "Failed to add card", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Card details</Label>
        <div className="p-3 rounded-md border bg-card">
          <CardElement
            options={cardElementOptions}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </div>
      </div>
      <DialogFooter className="gap-2 flex-wrap">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || !cardComplete || saving}>
          {saving ? "Saving..." : "Save Card"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function AccessibilitySettings() {
  const { toast } = useToast();

  const { data, isLoading } = useQuery<{ preferences: {
    dyslexiaFont?: boolean;
    textSize?: "default" | "large" | "extra-large";
    lineSpacing?: boolean;
    highContrast?: boolean;
    reducedMotion?: boolean;
  } }>({
    queryKey: ["/api/profile/accessibility"],
  });

  const prefs = data?.preferences || {};

  const mutation = useMutation({
    mutationFn: async (newPrefs: Record<string, unknown>) => {
      const res = await apiRequest("PUT", "/api/profile/accessibility", newPrefs);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile/accessibility"] });
      toast({ title: "Settings saved", description: "Your accessibility preferences have been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save preferences.", variant: "destructive" });
    },
  });

  const updatePref = (key: string, value: unknown) => {
    mutation.mutate({ ...prefs, [key]: value });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading preferences...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Accessibility className="w-5 h-5" />
          Accessibility Settings
        </CardTitle>
        <CardDescription>
          Customize your reading and viewing experience. These settings are optional and can be changed at any time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Reading</h3>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Dyslexia-Friendly Font</Label>
              <p className="text-sm text-muted-foreground">Use the Lexend font, designed for easier reading</p>
            </div>
            <Switch
              checked={!!prefs.dyslexiaFont}
              onCheckedChange={(checked) => updatePref("dyslexiaFont", checked)}
              disabled={mutation.isPending}
              data-testid="switch-dyslexia-font"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Text Size</Label>
              <p className="text-sm text-muted-foreground">Increase the size of text across the platform</p>
            </div>
            <Select
              value={prefs.textSize || "default"}
              onValueChange={(value) => updatePref("textSize", value)}
              disabled={mutation.isPending}
            >
              <SelectTrigger className="w-40" data-testid="select-text-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="large">Large</SelectItem>
                <SelectItem value="extra-large">Extra Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Increased Line Spacing</Label>
              <p className="text-sm text-muted-foreground">Add more space between lines of text for easier reading</p>
            </div>
            <Switch
              checked={!!prefs.lineSpacing}
              onCheckedChange={(checked) => updatePref("lineSpacing", checked)}
              disabled={mutation.isPending}
              data-testid="switch-line-spacing"
            />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Display</h3>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">High Contrast</Label>
              <p className="text-sm text-muted-foreground">Increase text contrast for better visibility</p>
            </div>
            <Switch
              checked={!!prefs.highContrast}
              onCheckedChange={(checked) => updatePref("highContrast", checked)}
              disabled={mutation.isPending}
              data-testid="switch-high-contrast"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Reduced Motion</Label>
              <p className="text-sm text-muted-foreground">Minimize animations and transitions throughout the platform</p>
            </div>
            <Switch
              checked={!!prefs.reducedMotion}
              onCheckedChange={(checked) => updatePref("reducedMotion", checked)}
              disabled={mutation.isPending}
              data-testid="switch-reduced-motion"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function NotificationSettings() {
  const { toast } = useToast();
  const supported = isPushSupported();
  const [permission, setPermission] = useState<NotificationPermission | null>(
    supported ? Notification.permission : null
  );
  const [deviceEnabled, setDeviceEnabled] = useState(false);
  const [deviceBusy, setDeviceBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getExistingSubscription().then((sub) => setDeviceEnabled(!!sub));
  }, [supported]);

  const { data: prefs, isLoading } = useQuery<{
    pushMessages: boolean;
    pushNotifications: boolean;
    pushReminders: boolean;
    hasSubscriptions: boolean;
  }>({
    queryKey: ["/api/push/prefs"],
  });

  const prefsMutation = useMutation({
    mutationFn: async (update: Record<string, boolean>) => {
      const res = await apiRequest("PUT", "/api/push/prefs", update);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/push/prefs"] });
      toast({ title: "Settings saved", description: "Your notification preferences have been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save preferences.", variant: "destructive" });
    },
  });

  const toggleDevice = async (checked: boolean) => {
    setDeviceBusy(true);
    try {
      if (checked) {
        const result = await enablePush();
        if (result === "granted") {
          setDeviceEnabled(true);
          queryClient.invalidateQueries({ queryKey: ["/api/push/prefs"] });
          toast({ title: "Notifications are on!", description: "This device will now get alerts." });
        } else if (result === "denied") {
          toast({
            title: "Notifications are blocked",
            description: "Allow notifications for EduFocal in your browser or device settings, then try again.",
            variant: "destructive",
          });
        } else if (result !== "default") {
          toast({ title: "Not available", description: "Notifications aren't available right now.", variant: "destructive" });
        }
        setPermission(supported ? Notification.permission : null);
      } else {
        await disablePush();
        setDeviceEnabled(false);
        queryClient.invalidateQueries({ queryKey: ["/api/push/prefs"] });
        toast({ title: "Notifications are off", description: "This device will no longer get alerts." });
      }
    } catch (err) {
      console.error("[WebPush] Toggle failed:", err);
      toast({ title: "Error", description: "Could not update notifications for this device.", variant: "destructive" });
    } finally {
      setDeviceBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notifications
        </CardTitle>
        <CardDescription>
          Choose which alerts you get on your devices. You can change these at any time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">This Device</h3>

          {!supported ? (
            <p className="text-sm text-muted-foreground">
              Notifications aren't available in this browser. On iPhone or iPad, add EduFocal to your Home Screen first
              (Share, then Add to Home Screen) and open it from there to turn on notifications.
            </p>
          ) : permission === "denied" ? (
            <p className="text-sm text-muted-foreground">
              Notifications are blocked for EduFocal on this device. To turn them on, allow notifications for
              edufocalacademy.com in your browser or device settings, then come back here.
            </p>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-base">Notifications on this device</Label>
                <p className="text-sm text-muted-foreground">Get alerts on this device even when EduFocal is closed</p>
              </div>
              <Switch
                checked={deviceEnabled}
                onCheckedChange={toggleDevice}
                disabled={deviceBusy}
                data-testid="switch-push-device"
              />
            </div>
          )}
        </div>

        <Separator />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">What You Get</h3>
          <p className="text-sm text-muted-foreground">
            These choices apply on every device where you've turned notifications on.
          </p>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Messages</Label>
              <p className="text-sm text-muted-foreground">When you get a new message</p>
            </div>
            <Switch
              checked={prefs?.pushMessages ?? true}
              onCheckedChange={(checked) => prefsMutation.mutate({ pushMessages: checked })}
              disabled={isLoading || prefsMutation.isPending}
              data-testid="switch-push-messages"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Platform notifications</Label>
              <p className="text-sm text-muted-foreground">Prizes, badges, friends and other updates</p>
            </div>
            <Switch
              checked={prefs?.pushNotifications ?? true}
              onCheckedChange={(checked) => prefsMutation.mutate({ pushNotifications: checked })}
              disabled={isLoading || prefsMutation.isPending}
              data-testid="switch-push-notifications"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-base">Reminders</Label>
              <p className="text-sm text-muted-foreground">Practice reminders and streak alerts</p>
            </div>
            <Switch
              checked={prefs?.pushReminders ?? true}
              onCheckedChange={(checked) => prefsMutation.mutate({ pushReminders: checked })}
              disabled={isLoading || prefsMutation.isPending}
              data-testid="switch-push-reminders"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
